import { FC, useEffect, useMemo, useRef, useState } from "react";
import styles from './bid-overview.module.scss';
import TooltipContainer from "../../components/shared/TooltipContainer/TooltipContainer";
import HTMLParser from "../../components/shared/HTMLParser/HTMLParser";
import BtButton from "../bt-button/bt-button";
import _ from "lodash";
import moment from "moment";
import { ReactComponent as TriangleWarningIcon } from '../../assets/svg/triangleWarningIcon.svg';
import { ReactComponent as MessageIcon } from '../../assets/svg/messageIcon.svg';
import { ReactComponent as DownloadIcon } from '../../assets/svg/download2.svg';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { RouteComponentProps, withRouter } from "react-router-dom";
import { formatAmount } from '../../util';
import DownloadButton from "../../components/shared/DownloadButton";
import { ServiceQuotation } from "../../services/service-quotation";
import Spinner from "../../components/shared/Spinner/Spinner";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import FilteringControl from "../../components/shared/FilteringControl/FilteringControl";
import Selector, { SelectorType } from "../../components/shared/Selector/Selector";
import serviceFiles, { FileNode as FileAreaNode } from "../../services/service-files";
import fileDownload from "js-file-download";
import { NotificationManager } from "react-notifications";

type HasId = {
  _id: string;
}

type HasName = {
  name: string;
}

type HasSlug = {
  slug: string;
}

type HasOrgId = {
  orgId: string;
}

type Supplier = {
  supplierId: HasId & HasName;
  message: string;
  reservations: string;
  attachments: any[];
  fileAreaBidFiles?: string;
  date: Date | null;
  quotationValidity: Date | null;
}

type DisplayColumn = {
  supplier: Supplier;
  versionIdx: number;
  refVersion: number;
  isLatest: boolean;
  supplierIndex: number;
}

type Props = RouteComponentProps & {
  modifiedSuppliers: any[];
  versionData: any[];
  showDefaultBidValidityDate: boolean;
  getVersionInState: (supplierId: string) => number;
  getQuotationVersions: (supplier: HasId & HasName) => any[]
  currentProject: HasSlug & HasName & HasOrgId;
  areaSlug: string;
  areaName: string;
}

type FileAreaAttachmentsCellProps = {
  fileAreaBidFiles: string;
  supplierName: string;
  projectName: string;
  areaName: string;
  version: number;
  orgId: string;
  t: (key: string, fallback?: string, vars?: any) => string;
}

const FileAreaAttachmentsCell: FC<FileAreaAttachmentsCellProps> = ({
  fileAreaBidFiles,
  supplierName,
  projectName,
  areaName,
  version,
  orgId,
  t,
}) => {
  const [files, setFiles] = useState<FileAreaNode[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [zipping, setZipping] = useState(false);
  const hasFetchedRef = useRef(false);

  const fetchFiles = async () => {
    if (hasFetchedRef.current) return files;
    hasFetchedRef.current = true;
    setLoading(true);
    try {
      const listing = await serviceFiles.listFiles(fileAreaBidFiles, '/');
      const found = ((listing?.children || []) as FileAreaNode[]).filter(c => c.type === 'FILE');
      setFiles(found);
      return found;
    } catch (error: any) {
      hasFetchedRef.current = false;
      const errorMsg = error?.response?.data?.error ?? (error as Error).message;
      NotificationManager.error(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fileName = t(
    'attachments.fileName',
    'Anbud från {{supplierName}} på {{projectName}} - {{areaName}} - V{{version}}.zip',
    { supplierName, projectName, areaName, version }
  );

  const downloadZip = async () => {
    if (zipping) return;
    setZipping(true);
    try {
      const list = files ?? (await fetchFiles());
      if (!list || list.length === 0) {
        NotificationManager.warning(t('attachments.noFiles', 'Inga filer att ladda ner'));
        return;
      }
      const blob = await serviceFiles.downloadZip(fileAreaBidFiles, list.map(f => f._id), orgId);
      fileDownload(blob, fileName, 'application/zip');
    } catch (error: any) {
      const errorMsg = error?.response?.data?.error ?? (error as Error).message;
      NotificationManager.error(errorMsg);
    } finally {
      setZipping(false);
    }
  };

  return (
    <div className={styles.otherContentIcon} onMouseEnter={() => { fetchFiles(); }}>
      <TooltipContainer renderReferenceComponent={(className, ref) =>
        zipping ? (
          <BtButton class={cx(className, styles.flatIconButton)} ref={ref} color="white" size="xxs" disabled={true}>
            <Spinner progress={0} size="xxs" />
          </BtButton>
        ) : (
          <BtButton class={cx(className, styles.flatIconButton)} ref={ref} color="white" size="xxs" onClick={downloadZip}>
            <DownloadIcon className={styles.downloadIcon} />
          </BtButton>
        )
      }>
        <div style={{ minWidth: '250px' }}>
          <strong>{t('attachments.fileArea.title', 'Filer (ladda ner alla som .zip):')}</strong>
          {loading && <div style={{ marginTop: 8 }}>{t('attachments.loading', 'Laddar...')}</div>}
          {!loading && files && files.length > 0 && (
            <ul>
              {files.map(f => <li key={"fileArea_" + f._id}>{f.name}</li>)}
            </ul>
          )}
          {!loading && files && files.length === 0 && (
            <div style={{ marginTop: 8 }}>{t('attachments.empty', 'Inga filer.')}</div>
          )}
        </div>
      </TooltipContainer>
    </div>
  );
};

const BidOverview : FC<Props> = ({
  modifiedSuppliers,
  versionData,
  showDefaultBidValidityDate,
  getVersionInState,
  getQuotationVersions,
  currentProject,
  areaSlug,
  areaName,
}) => {
  const [t] = useTranslation('projects', { keyPrefix: 'singlePurchase.bidOverview' });
  const keyOfColumn = (col: DisplayColumn) => `${col.supplier.supplierId._id}_v${col.versionIdx}`;
  const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set());
  const [isCollapseAllEnabled, setIsCollapseAllEnabled] = useState(false);
  const [isExpandAllEnabled, setIsExpandAllEnabled] = useState(true);
  const [hoveredSupplierIndex, setHoveredSupplierIndex] = useState<number | null>(null);
  const nameOption: SelectorType = { label: t('sort.name', 'Namn'), value: 'name' };
  const [sortBy, setSortBy] = useState<SelectorType>(nameOption);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const getSupplierIdsWithMultipleVersions = () => {
    const supplierIdsWithMultipleVersions: string[] = [];
    for (let i = 0; i < modifiedSuppliers.length; i++) {
      const supplierId = modifiedSuppliers[i].supplierId._id;
      const versions = versionData[0]?.versions;
      let versionCount = 0;
      for (let j = 0; j < versions?.length; j++) {
        const { supplierBids } = versions[j];
        if (supplierBids?.find((b: { supplierId: string }) => b.supplierId === supplierId))
          versionCount++;
      }
      if (versionCount > 1) supplierIdsWithMultipleVersions.push(supplierId);
    }
    return supplierIdsWithMultipleVersions;
  }

  useEffect(() => {
    const supplierIdsWithMultipleVersions = getSupplierIdsWithMultipleVersions();
    const allExpanded = supplierIdsWithMultipleVersions.every(id => expandedSuppliers.has(id));
    const noneExpanded = supplierIdsWithMultipleVersions.every(id => !expandedSuppliers.has(id));
    setIsExpandAllEnabled(allExpanded);
    setIsCollapseAllEnabled(noneExpanded);
  }, [expandedSuppliers]);

  const toggleExpandSupplier = (supplierId: string) => {
    setExpandedSuppliers(prev => {
      const next = new Set(prev);
      if (next.has(supplierId)) next.delete(supplierId); else next.add(supplierId);
      return next;
    });
  };

  const getPricesForVersionAndSupplier = (supplierId: string, versionIdx: number) => {
    const quoteVersions = _.head(versionData);
    if (!quoteVersions?.versions?.[versionIdx]) return [];
    const supplierBids = quoteVersions.versions[versionIdx].supplierBids?.find(
      (x: {supplierId: string}) => x.supplierId === supplierId,
    );
    let priceDetails = quoteVersions.versions[versionIdx].priceDetails;

    if (supplierBids?.priceDetails) {
      return supplierBids.priceDetails.map((bids: { price: string, priceType: string }) => {
        const formattedPrice = formatAmount(bids.price);
        priceDetails = priceDetails.filter((pd: { priceType?: HasId }) => pd.priceType);
        const findPriceDetail = priceDetails.find((details: { priceType: HasId }) => details.priceType._id == bids.priceType);
        return {
          name: findPriceDetail?.priceType?.name,
          unit: findPriceDetail?.unit ?? t('fallbackPriceUnit', 'kr'),
          price: formattedPrice ? formattedPrice : '-',
          rawPrice: parseFloat(bids.price),
        };
      });
    }
    return priceDetails?.map((p: { priceType: HasName }) => ({ name: p.priceType.name })) ?? [];
  };

  const getDemandsForVersionAndSupplier = (supplierId: string, versionIdx: number) => {
    const quoteVersions = _.head(versionData);
    if (!quoteVersions?.versions?.[versionIdx]) return [];
    const supplierBids = quoteVersions.versions[versionIdx].supplierBids?.find(
      (x: { supplierId: string }) => x.supplierId === supplierId,
    );
    const demands = quoteVersions.versions[versionIdx].demands;
    if (demands && supplierBids?.demands) {
      return demands.map((d: { demandId: HasId & HasName }) => ({
        name: d.demandId.name,
        value: !!supplierBids.demands.find((sd: { demandId: string }) => sd.demandId === d.demandId._id),
      }));
    }
    return demands?.map((d: { demandId: HasId & HasName }) => ({ name: d.demandId.name })) ?? [];
  };
  
  const getOtherContentForVersionAndSupplier = (
    supplierId: string,
    versionIdx: number,
  ): Pick<Supplier, 'message' | 'reservations' | 'attachments'> => {
    const quoteVersions = _.head(versionData);
    const supplierBids = quoteVersions?.versions?.[versionIdx]?.supplierBids?.find(
      (x: { supplierId: string }) => x.supplierId === supplierId,
    );
    return {
      message: supplierBids?.message ?? '',
      reservations: supplierBids?.content ?? '',
      attachments: supplierBids?.attachments ?? [],
    };
  };

  const distinctPriceFieldNames = useMemo((): string[] => {
    const names = new Set<string>();
    const quoteVersions = _.head(versionData);
    for (const version of quoteVersions?.versions ?? []) {
      for (const pd of version.priceDetails ?? []) {
        if (pd.priceType?.name) names.add(pd.priceType.name);
      }
    }
    return Array.from(names);
  }, [versionData]);

  const getQuoteValidity = (validity : Date | null) => {
    return validity ? moment(validity).format('YYYY-MM-DD') : ""
  };

  const getDisplayColumns = (suppliers: Supplier[]): DisplayColumn[] => {
    return suppliers.flatMap((supplier, supplierIndex) => {
      const supplierId = supplier.supplierId._id;
      const versions = getQuotationVersions(supplier.supplierId);
      const isExpanded = expandedSuppliers.has(supplierId);

      if (!isExpanded || versions.length <= 1) {
        const versionIdx = getVersionInState(supplierId) ?? (_.last(versions) as any)?.refVersion ?? 0;
        return [{ supplier, versionIdx, refVersion: versionIdx, isLatest: true, supplierIndex }];
      }

      return [...versions].reverse().map((v: any, i: number) => ({
        supplier,
        versionIdx: v.refVersion,
        refVersion: v.refVersion,
        isLatest: i === 0,
        supplierIndex,
      }));
    });
  };

  const colClass = (col: DisplayColumn, ...extra: (string | undefined | false)[]) =>
    cx( {[`${styles.colGray}`]: col.supplierIndex % 2 === 0}, ...extra) || undefined;

  const colTdProps = (col: DisplayColumn, ...extra: (string | undefined | false)[]) => ({
    className: colClass(col, ...extra),
    onMouseEnter: () => setHoveredSupplierIndex(col.supplierIndex),
    onMouseLeave: () => setHoveredSupplierIndex(null),
  });

  const renderPriceFieldRows = (displayColumns: DisplayColumn[]) => {
    const prices = displayColumns.map(col => getPricesForVersionAndSupplier(col.supplier.supplierId._id, col.versionIdx));
    const distinctFields: string[] = Array.from(prices.reduce((set, pricesForSupplier) => {
      pricesForSupplier.map((price: HasName) => set.add(price.name));
      return set;
    }, new Set<string>()));
    return distinctFields.map(name =>
      <tr key={"pricerow_" + name}>
        <td key="name">{name}</td>
        {displayColumns.map((col, i) => {
          const price = prices[i].find((p: HasName) => p.name === name);
          return <td key={keyOfColumn(col) + "_price_" + name} {...colTdProps(col, styles.right)}>{price?.price}&nbsp;{price?.unit}</td>;
        })}
      </tr>
    );
  }

  const renderDemandRows = (displayColumns: DisplayColumn[]) => {
    const demands = displayColumns.map(col => getDemandsForVersionAndSupplier(col.supplier.supplierId._id, col.versionIdx));
    const distinctFields: string[] = Array.from(demands.reduce((set, demandsForSupplier) => {
      demandsForSupplier.map((demand: HasName) => set.add(demand.name));
      return set;
    }, new Set<string>()));
    return distinctFields.map(name =>
      <tr key={"demandrow_" + name}>
        <td key="name">{name}</td>
        {displayColumns.map((col, i) => {
          const demand = demands[i].find((p: HasName) => p.name === name);
          if (demand && demand.value !== undefined) {
            return <td key={keyOfColumn(col) + "_demand_" + name} {...colTdProps(col)}>
              <div className={styles.center}>
                {demand.value ? (
                  <div className="collective-agreementFlag true"><CheckIcon /></div>
                ) : (
                  <div className="collective-agreementFlag false"><CloseIcon /></div>
                )}
              </div>
            </td>;
          }
          return <td key={keyOfColumn(col) + "_demand_" + name} {...colTdProps(col)}>&nbsp;</td>;
        })}
      </tr>
    );
  };

  const renderMessage = (message: string) => {
    return message ? (
      <span className={styles.otherContentIcon}>
        <TooltipContainer renderReferenceComponent={(className, ref) => <MessageIcon className={className} ref={ref} style={{ width: '18px', fill: 'var(--gray-700)'}}/>}>
          <div style={{ minWidth: '250px' }}>
            <strong>{t('message.label', 'Meddelande:')}</strong>
            <HTMLParser data={message}/>
          </div>
        </TooltipContainer>
      </span>
    ) : null;
  };

  const renderReservations = (reservations: string) => {
    return reservations ? (
      <span className={styles.otherContentIcon}>
        <TooltipContainer renderReferenceComponent={(className, ref) => <TriangleWarningIcon className={className} ref={ref} style={{ width: '19px', fill: 'var(--gray-700)'}}/>}>
          <div style={{ minWidth: '250px' }}>
            <strong>{t('reservations.label', 'Reservationer:')}</strong>
            <HTMLParser data={reservations}/>
          </div>
        </TooltipContainer>
      </span>
    ) : null;
  };

  const renderFileAreaAttachments = (supplier: Supplier, versionIdx: number) => {
    const supplierBid = versionData[0]?.versions?.[versionIdx]?.supplierBids?.find(
      (b: { supplierId: string }) => b.supplierId === supplier.supplierId._id
    );
    const fileAreaBidFiles = supplierBid?.fileAreaBidFiles;
    if (!fileAreaBidFiles) return null;
    return (
      <FileAreaAttachmentsCell
        fileAreaBidFiles={fileAreaBidFiles}
        supplierName={supplier.supplierId.name}
        projectName={currentProject.name}
        areaName={areaName}
        version={versionIdx + 1}
        orgId={currentProject.orgId}
        t={t}
      />
    );
  };

  const renderAttachments = (supplier: Supplier, versionIdx: number, attachments: any[]) => {
    const projectSlug = currentProject.slug;
    const projectName = currentProject.name;
    const supplierId = supplier.supplierId._id;
    const supplierName = supplier.supplierId.name;
    const quoteVersionNo = versionIdx;
    if (attachments.length === 0) return null;
    return (<div className={styles.otherContentIcon}>
        <TooltipContainer renderReferenceComponent={(className, ref) =>
          <DownloadButton
            url={ServiceQuotation.getDownloadQuotationResponseFilesInZipURL({
              projectSlug,
              areaSlug,
              supplierId,
              versionNumber: quoteVersionNo,
              orgId: currentProject.orgId,
            })}
            fileName={t('attachments.fileName', 'Anbud från {{supplierName}} på {{projectName}} - {{areaName}} - V{{version}}.zip', { supplierName, projectName, areaName, version: quoteVersionNo + 1 })}
            render={({ downloading, progress, onClick }) => (
              downloading ? (
                <BtButton class={cx(className, styles.flatIconButton)} ref={ref} color="white" size="xxs" disabled={true}>
                  <Spinner progress={progress} size="xxs" />
                </BtButton>
              ) : (
                <BtButton class={cx(className, styles.flatIconButton)} ref={ref} color="white" size="xxs" onClick={onClick}>
                  <DownloadIcon className={styles.downloadIcon}/>
                </BtButton>
              )
            )}
          />
        }>
          <div style={{ minWidth: '250px' }}>
            <strong>{t('attachments.oldTitle', 'Äldre Bilagor (ladda ner alla som .zip):')}</strong>
            <ul>
              {attachments.map(a => <li key={"attachment_" + a._id}>{a.fileName}</li>)}
            </ul>
          </div>
        </TooltipContainer>
    </div>);
  }

  const renderOtherContent = (supplier: Supplier, versionIdx: number) => {
    const { message, reservations, attachments } = getOtherContentForVersionAndSupplier(supplier.supplierId._id, versionIdx);
      return <div className={styles.otherContent}>
        {renderMessage(message)}
        {renderReservations(reservations)}
        {renderFileAreaAttachments(supplier, versionIdx)}
        {renderAttachments(supplier, versionIdx, attachments)}
      </div>;
  };

  const renderColumnBasedTable = () => {
    const sortField = sortBy?.value;
    const dir = sortDir === 'asc' ? 1 : -1;
    const suppliers = !sortField || sortField === 'name'
      ? [...(modifiedSuppliers ?? [])].sort((a, b) =>
        dir * ((a?.name ?? '').toLowerCase() < (b?.name ?? '').toLowerCase() ? -1 : 1)
      )
      : [...(modifiedSuppliers ?? [])].sort((a, b) => {
        const getRawPrice = (supplierId: string) => {
          const versions: any[] = (_.head(versionData) as any)?.versions ?? [];
          const currentIdx = getVersionInState(supplierId) ?? 0;
          const orderedIndices = [currentIdx, ...Array.from({ length: versions.length }, (_, i) => i).filter(i => i !== currentIdx).reverse()];
          for (const idx of orderedIndices) {
            const bid = versions[idx]?.supplierBids?.find((b: any) => b.supplierId === supplierId);
            if (!bid?.priceDetails) continue;
            const priceEntry = bid.priceDetails.find((pd: any) => {
              const fieldDef = versions[idx]?.priceDetails?.find((vpd: any) => vpd.priceType?._id == pd.priceType);
              return fieldDef?.priceType?.name === sortField;
            });
            if (priceEntry && formatAmount(priceEntry.price) !== '') return parseFloat(priceEntry.price);
          }
          return Infinity;
        };
        const aPrice = getRawPrice(a.supplierId._id);
        const bPrice = getRawPrice(b.supplierId._id);
        if (aPrice === Infinity && bPrice === Infinity) return 0;
        if (aPrice === Infinity) return 1;
        if (bPrice === Infinity) return -1;
        return dir * (aPrice - bPrice);
      });
    const displayColumns = getDisplayColumns(suppliers);
    return (
      <table className={styles.table}>
        <thead>
          <tr>
            <th>&nbsp;</th>
            {suppliers.map((supplier, supplierIndex) => {
              const supplierId = supplier.supplierId._id;
              const versions = getQuotationVersions(supplier.supplierId);
              const isExpanded = expandedSuppliers.has(supplierId);
              const colSpan = isExpanded && versions.length > 1 ? versions.length : 1;

              return (
                <th key={supplierId} className={cx(styles.supplierName, { [`${styles.colGray}`]: supplierIndex % 2 === 0, [`${styles.columnHovered}`]: hoveredSupplierIndex === supplierIndex })} colSpan={colSpan}>
                  <div className={styles.center} style={{gap: "8px"}}>
                    <span>
                      {supplier.supplierId.name}
                    </span>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>{t('table.header.version', 'Version')}</th>
            {displayColumns.map((col) => {
              const { isLatest, supplier } = col;
              const supplierId = supplier.supplierId._id;
              const isExpanded = expandedSuppliers.has(supplierId);
              const versions = getQuotationVersions(supplier.supplierId);
              const canExpand = versions.length > 1;

              return (
              <td key={keyOfColumn(col)} {...colTdProps(col)}>
                <div className={styles.center}>
                  <div className={styles.center}>
                    <span style={{ fontWeight: 600 }}>{`V${col.versionIdx + 1}`}</span>
                    {isLatest && canExpand && (
                      <BtButton
                        color='white'
                        size='xxs'
                        style={{ width: 'fit-content' }}
                        onClick={() => toggleExpandSupplier(supplierId)}
                      >
                        {isExpanded ? t('buttons.collapseSingle', 'Dölj äldre') :  t('buttons.expandSingle', 'Visa alla')}
                      </BtButton>
                    )}
                  </div>
                </div>
              </td>
            )})}
          </tr>
          <tr className={styles.empty}>
            <th>&nbsp;</th>
            {displayColumns.map((col) => <td key={keyOfColumn(col)} {...colTdProps(col)}>&nbsp;</td>)}
          </tr>
          <tr className={styles.empty}>
            <th>{t('table.header.priceFields', 'Prisfält')}</th>
            {displayColumns.map((col) => <td key={keyOfColumn(col)} {...colTdProps(col)}>&nbsp;</td>)}
          </tr>
          {renderPriceFieldRows(displayColumns)}
          <tr className={styles.empty}>
            <th>&nbsp;</th>
            {displayColumns.map((col) => <td key={keyOfColumn(col)} {...colTdProps(col)}>&nbsp;</td>)}
          </tr>
          <tr className={styles.empty}>
            <th>{t('table.header.requirements', 'Skallkrav')}</th>
            {displayColumns.map((col) => <td key={keyOfColumn(col)} {...colTdProps(col)}>&nbsp;</td>)}
          </tr>
          {renderDemandRows(displayColumns)}
          <tr className={styles.empty}>
            <th>&nbsp;</th>
            {displayColumns.map((col) => <td key={keyOfColumn(col)} {...colTdProps(col)}>&nbsp;</td>)}
          </tr>
          <tr>
            <th>{t('table.header.otherContent', 'Övrigt innehåll')}</th>
            {displayColumns.map(col => (
                <td key={keyOfColumn(col)} {...colTdProps(col)}>
                  {renderOtherContent(col.supplier, col.versionIdx)}
                </td>
              )
            )}
          </tr>
          {showDefaultBidValidityDate && (
            <tr>
              <th>{t('table.header.validity', 'Giltigt till')}</th>
              {displayColumns.map(col => (
                  <td key={keyOfColumn(col)} {...colTdProps(col)}>
                    <div className={styles.center}>{getQuoteValidity(col.supplier?.quotationValidity)}</div>
                  </td>
                )
              )}
            </tr>
          )}
        </tbody>
      </table>
    )
  }

  return (
    <div className={styles.container}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: 'space-between', flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <p style={{ marginRight: "16px" }}>{t('buttons.description', 'Visa anbud')}</p>
          <FilteringControl
            value={''}
            className={styles.filteringControlContainer}
            options={[
              {
                label: t('buttons.collapseAll', 'Senaste versionen för varje leverantör'),
                value: 'all',
                onSelection: () => {
                  modifiedSuppliers.map(s => {
                    if (expandedSuppliers.has(s.supplierId._id)) toggleExpandSupplier(s.supplierId._id);
                  });
                },
                disabled: isCollapseAllEnabled
              },
              {
                label: t('buttons.expandAll', 'Alla versioner'),
                value: 'latest',
                onSelection: () => {
                  modifiedSuppliers.map(s => {
                    if (!expandedSuppliers.has(s.supplierId._id)) toggleExpandSupplier(s.supplierId._id);
                  });
                },
                disabled: isExpandAllEnabled
              },
            ]}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", paddingRight: '2px' }}>
          <p style={{ margin: 0, whiteSpace: "nowrap" }}>{t('sort.label', 'Sortera på:')}</p>
          <Selector
            value={sortBy}
            onChange={(val) => {
              const newVal = val as SelectorType;
              setSortBy(newVal);
            }}
            options={[
              nameOption,
              ...distinctPriceFieldNames.map((name: string) => ({ label: name, value: name })),
            ]}
            isSearchable={false}
            mini={false}
            className={styles.bidOverviewSelector}
          />
          <BtButton
            color="white"
            size="base"
            onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
          >
            <span className={cx(styles.sortArrow, { [styles.desc]: sortDir === 'desc' })}>↑</span>
          </BtButton>
        </div>
      </div>
      {renderColumnBasedTable()}
    </div>
  )
}

export default withRouter(BidOverview);
